/**
 * CORE — the fixed-timestep Simulation: the live, stepping game.
 *
 * Wraps the deterministic PhysicsWorld and implements the locked `Simulation`
 * interface. Responsibilities:
 *   - initialize Rapier's wasm (the locked surface is async for this reason),
 *   - advance exactly one FIXED_DT tick per `step`, quantizing + recording input,
 *   - expose an immutable GameState snapshot each tick (Render's only window),
 *   - assemble ReplayData (sparse input timeline + metrics + stateHash) once the
 *     run is finished/dnf.
 *
 * Determinism: the score-bearing math is the pure-JS PhysicsWorld. Input is
 * quantized to int16 BEFORE it touches physics (so record == playback), and the
 * timeline is sparse — only ticks where the quantized steer changes are stored.
 *
 * Owned by: Core. Implements: Simulation (locked surface in index.ts).
 */

import RAPIER from '@dimforge/rapier3d-compat';
import {
  SCHEMA_VERSION,
  SIM_VERSION,
  TICK_RATE,
  MAX_RUN_TICKS,
} from '@/types';
import type {
  CourseSpec,
  GameState,
  ReplayData,
  InputEvent,
  ScoreMetrics,
  RunStatus,
} from '@/types';
import type { Simulation, SteerInput } from './index';
import { PhysicsWorld } from './physics';
import { quantizeSteer, dequantizeSteer } from './steer';
import { buildMetrics } from './scoring';
import { hashFinalState } from './hash';

/** Ensure Rapier's wasm is initialized exactly once per process. Idempotent. */
let rapierReady: Promise<void> | null = null;
function ensureRapier(): Promise<void> {
  if (rapierReady === null) {
    rapierReady = RAPIER.init();
  }
  return rapierReady;
}

class SimulationImpl implements Simulation {
  readonly spec: CourseSpec;
  private readonly world: PhysicsWorld;
  /** Sparse, strictly-increasing-by-tick input timeline (quantized). */
  private readonly events: InputEvent[] = [];
  /** Last quantized steer applied (to detect changes for the sparse timeline). */
  private lastQuantX = 0;
  private lastQuantY = 0;
  private hasEmitted = false;

  constructor(spec: CourseSpec) {
    this.spec = spec;
    this.world = new PhysicsWorld(spec);
  }

  step(input: SteerInput): GameState {
    const s = this.world.state;
    // A finished/dnf sim is frozen; further steps are no-ops returning the state.
    if (s.status !== 'running') return this.snapshot();

    // Quantize input, then record a sparse event only when it changes.
    const qx = quantizeSteer(input.steerX);
    const qy = quantizeSteer(input.steerY);
    const tickToRecord = s.tick; // the tick this input takes effect on (pre-step)
    if (!this.hasEmitted || qx !== this.lastQuantX || qy !== this.lastQuantY) {
      this.events.push({ tick: tickToRecord, steerX: qx, steerY: qy });
      this.lastQuantX = qx;
      this.lastQuantY = qy;
      this.hasEmitted = true;
    }

    // Physics sees the DEQUANTIZED steer, so record and playback are identical.
    // steerY drives forward (+Z heading), steerX steers laterally.
    this.world.step(dequantizeSteer(qx), dequantizeSteer(qy));

    // Hard run cap: anything not finished by MAX_RUN_TICKS is a DNF.
    if (this.world.state.status === 'running' && this.world.state.tick >= MAX_RUN_TICKS) {
      this.world.state.status = 'dnf';
    }

    return this.snapshot();
  }

  getState(): GameState {
    return this.snapshot();
  }

  getReplay(): ReplayData {
    const metrics = this.computeMetrics();
    const s = this.world.state;
    const stateHash = hashFinalState(s.position, s.rotation, s.velocity, metrics);
    // The faithful run length is exactly the number of integration steps taken,
    // which the world tracks as `s.tick` (incremented once per world.step). The
    // run may have been truncated by the caller before finishing/dnf-ing, so we
    // record the ACTUAL tick count — not a constant cap — so that resimulating
    // this replay reproduces the same number of steps (and thus same state/hash).
    const totalTicks = s.tick;
    return {
      schemaVersion: SCHEMA_VERSION,
      simVersion: SIM_VERSION,
      seed: this.spec.seed,
      dateKey: this.spec.dateKey,
      tickRate: TICK_RATE,
      totalTicks,
      inputEvents: this.events.slice(),
      metrics,
      stateHash,
    };
  }

  reset(): void {
    this.world.reset();
    this.events.length = 0;
    this.lastQuantX = 0;
    this.lastQuantY = 0;
    this.hasEmitted = false;
  }

  private computeMetrics(): ScoreMetrics {
    const s = this.world.state;
    return buildMetrics({
      status: s.status,
      finishTick: s.tick,
      checkpointsReached: s.checkpointIndex,
      distance: s.distance,
    });
  }

  private snapshot(): GameState {
    const s = this.world.state;
    const status: RunStatus = s.status;
    return {
      tick: s.tick,
      status,
      marble: {
        position: [s.position[0], s.position[1], s.position[2]],
        rotation: [s.rotation[0], s.rotation[1], s.rotation[2], s.rotation[3]],
      },
      velocity: [s.velocity[0], s.velocity[1], s.velocity[2]],
      checkpointIndex: s.checkpointIndex,
      elapsedMs: (s.tick / TICK_RATE) * 1000,
    };
  }
}

/** Create a live simulation. Async because Rapier's wasm must initialize first. */
export async function createSimulation(spec: CourseSpec): Promise<Simulation> {
  await ensureRapier();
  return new SimulationImpl(spec);
}

/**
 * Re-run a replay's input timeline against a freshly generated course and return
 * authoritative metrics + the recomputed stateHash. Used by verifyReplay. The
 * `course` must already be generated from `replay.seed` by the caller.
 *
 * Replays the SPARSE timeline by holding the most recent event's steer until the
 * next event — exactly mirroring how the live run produced it.
 *
 * `totalTicks` is the authoritative run length recorded by the live run
 * (ReplayData.totalTicks): the exact number of integration steps it performed.
 * We replay EXACTLY that many steps so the resimulation reproduces the same
 * final state — the live run may have been truncated by its caller before the
 * marble finished or fell out, and the sparse event list alone cannot encode
 * that length (it only holds the last STEER CHANGE). Replaying a constant cap
 * instead would over-simulate a truncated run and diverge. We still stop early
 * if the world reaches a natural finished/dnf state, exactly as the live run
 * would have, and clamp totalTicks to the hard cap as a safety bound.
 */
export function resimulate(
  course: CourseSpec,
  events: readonly InputEvent[],
  totalTicks: number,
): { metrics: ScoreMetrics; stateHash: string } {
  const world = new PhysicsWorld(course);

  // Build a fast lookup from tick -> steer for the sparse events.
  let eventCursor = 0;
  let curX = 0;
  let curY = 0;

  // Replay exactly the recorded number of steps (bounded by the hard cap). A
  // step advances the world from `tick` to `tick + 1`, mirroring the live run
  // where each `step` records the pre-step tick then advances once.
  const stepCount = Math.min(Math.max(0, totalTicks | 0), MAX_RUN_TICKS);
  for (let tick = 0; tick < stepCount; tick++) {
    // Apply any events that take effect on this tick (there can be at most one
    // per tick in a well-formed timeline, but loop to be robust).
    while (eventCursor < events.length && events[eventCursor].tick === tick) {
      curX = events[eventCursor].steerX;
      curY = events[eventCursor].steerY;
      eventCursor++;
    }
    if (world.state.status !== 'running') break;
    world.step(dequantizeSteer(curX), dequantizeSteer(curY));
    // Mirror the live DNF cap: if the world is still running at the hard cap,
    // it is a DNF (the live run applies the identical rule in SimulationImpl.step).
    if (world.state.status === 'running' && world.state.tick >= MAX_RUN_TICKS) {
      world.state.status = 'dnf';
      break;
    }
  }

  const s = world.state;
  const metrics = buildMetrics({
    status: s.status,
    finishTick: s.tick,
    checkpointsReached: s.checkpointIndex,
    distance: s.distance,
  });
  const stateHash = hashFinalState(s.position, s.rotation, s.velocity, metrics);
  return { metrics, stateHash };
}
